// @ts-nocheck — upstream tRPC + TypeScript fetch overload incompatibility
"use client";

/**
 * tRPC React Provider
 *
 * Wraps the app with React Query and tRPC providers.
 * Must be used in a Client Component.
 */
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import {
  httpBatchLink,
  httpLink,
  isNonJsonSerializable,
  splitLink,
  TRPCClientError,
} from "@trpc/client";
import { useState } from "react";
import superjson from "superjson";
import { getClientHost, isEmbeddedSurface } from "@/lib/host";
import { magnusClient } from "@/lib/magnus-client";
import { trpc } from "./client";

let redirecting = false;

function handleAuthError(error: unknown) {
  if (redirecting) {
    return;
  }
  if (error instanceof TRPCClientError && error.data?.code === "UNAUTHORIZED") {
    // In an embedded copilot (Office add-in via MSAL NAA, or the Procore
    // panel via its sign-in popup), auth is handled in-surface — never
    // redirect to the Better Auth SSO flow (it would open a mismatched
    // browser tab / break out of the iframe). The host's auth gate overlays
    // the sign-in screen if needed.
    const host = getClientHost();
    if (isEmbeddedSurface(host)) {
      console.warn(
        "[Magnus] tRPC 401 — session not yet established, skipping SSO redirect"
      );
      return;
    }
    // During logout the session dies while queries are still in flight; the
    // resulting 401s must not hijack the intentional /logout → /login
    // navigation. Redirecting here with callbackUrl=/logout would silently
    // re-authenticate at Entra and land the user back on /logout — an
    // infinite sign-out/sign-in loop. Same for /login: the user is already
    // at the recovery surface.
    const { pathname } = window.location;
    if (pathname === "/logout" || pathname === "/login") {
      return;
    }
    redirecting = true;
    // `prompt=login` forces a genuine interactive re-authentication. A 401
    // mid-session means the session row was revoked (e.g. the server signs
    // users out when their Microsoft grant needs interactive re-auth —
    // ADR-072) or expired; silent SSO here would let Entra re-issue tokens
    // off the same stale browser session without re-running MFA/Conditional
    // Access, landing the user in a sign-out loop.
    window.location.href =
      "/api/auth/sso?callbackUrl=" +
      encodeURIComponent(window.location.pathname) +
      "&prompt=login";
  }
}

export function TRPCProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({ onError: handleAuthError }),
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  const [trpcClient] = useState(() => {
    // Auth (session cookie + Office add-in Bearer header) is applied by
    // the shared Magnus SDK fetch — see `lib/magnus-client`.
    const jsonHttp = {
      url: "/api/trpc",
      transformer: superjson,
      fetch: magnusClient.fetch,
    };
    // `context.keepalive` sends one unbatched request that outlives the page,
    // for the reader analytics flush on `pagehide` (ADR-125 §10).
    const keepaliveHttp = {
      ...jsonHttp,
      fetch: (input, init) =>
        magnusClient.fetch(input, { ...init, keepalive: true }),
    };
    return trpc.createClient({
      links: [
        splitLink({
          condition: (op) => op.context.keepalive === true,
          true: httpLink(keepaliveHttp),
          false: splitLink({
            // httpBatchLink waits for every procedure in the request. FormData
            // cannot go in that JSON batch; `context.skipBatch` is the same
            // escape hatch for a JSON caller that must resolve on its own.
            condition: (op) =>
              isNonJsonSerializable(op.input) || op.context.skipBatch === true,
            true: httpLink(jsonHttp),
            false: httpBatchLink(jsonHttp),
          }),
        }),
      ],
    });
  });

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}
